import React from "react";

/* Defines the structure for each reason item */
type Reason = {
  number: string;
  title: string;
  body: string;
};

/* List of reasons */
const reasons: Reason[] = [
  {
    number: "1",
    title: "Public Trust and Assurance",
    body:
      "Audited financial statements provide critical assurance to legislators, regulatory agencies, and the public. An independent opinion assures all, regardless of political affiliation or position, that the information presented is accurate, complete, and free from bias. Without independence, that assurance can collapse, and with it, erode public trust.",
  },
  {
    number: "2",
    title: "Objectivity and Impartiality",
    body:
      "When independence is compromised, so is objectivity. Professional judgment must be exercised free from obligations or relationships that could impair impartiality. This is the cornerstone of the Yellow Book’s requirement that auditors be free from both external and organizational impairments to their independence.",
  },
  {
    number: "3",
    title: "Enhanced Reliability of Financial Reports",
    body:
      "Reliable financial reporting is indispensable in both the private and public sectors. Financial statements are only as reliable as the independence of the auditor who reviews them. Independence ensures that audit opinions are credible. Independence enhances and supports sound decision-making. Such decisions could include areas of public policy, legislative deliberations and even the decisions of individual voters as they make choices at all levels of government.",
  },
  {
    number: "4",
    title: "Public Protection",
    body:
      "At its heart, independence serves to protect the public. High-quality, independent audits act as safeguards for taxpayers by ensuring resources are properly managed, transactions are accurately reported and accountability is always demonstrated. They are the mechanism by which elected officials, government entities, and citizens can evaluate the stewardship of public resources.",
  },
  {
    number: "5",
    title: "Ethical Responsibility",
    body:
      "Auditors are watchdogs of integrity in financial reporting. Our ethical duty extends beyond any relationship—we serve the public interest. Ethical responsibility requires not only technical accuracy but also an unwavering commitment to independence. We avoid circumstances that could compromise, or appear to compromise, that responsibility—a responsibility taken very seriously by every member of my office.",
  },
];

/* Taxpayer's Takeaway component */
const TaxpayersTakeaway: React.FC = () => {
  return (
    /* Main content section */
    <section className="tu-takeawayBlock" aria-labelledby="tu-takeaway-title">
      <div className="tu-takeawayOuter">

        {/* Page header */}
        <header className="tu-takeawayHeader">
          <h2 id="tu-takeaway-title" className="tu-takeawayTitle">
            Taxpayer&apos;s Takeaway
          </h2>

          <p className="tu-takeawayLead">
            Every audit tells a story. Every dollar has a purpose. Our mission is to ensure that Utah taxpayers understand exactly how their money is being used and whether it's making the impact it should.
          </p>
        </header>

        <div className="tu-takeawayBody">

          {/* Independence overview */}
          <section className="tu-takeawaySection">
          <h3 className="tu-takeawayH3 tu-takeawayH3--main">
                Independence: A Key Ingredient to Build Public Trust
          </h3>

            <p className="tu-takeawayP">
              CPAs understand that independence is not just a professional standard—it is key to build public trust in their work. In government auditing, independence becomes even more essential because we are entrusted with evaluating how public funds—collected from taxpayers and fee payers—are safeguarded, allocated, and expended.
            </p>

            {/* Constitutional authority callout */}
            <div className="tu-takeawayCallout" role="note">
              <p className="tu-takeawayP tu-mb0">
                The Office of the Utah State Auditor is uniquely structured to ensure independence in both fact and appearance. Established under Article VII, Section 15 of the Utah Constitution and empowered by Utah Code §67-3-1, the Office of the Utah State Auditor operates independently of any other branch (executive, legislative or judicial) or division of the state.
              </p>
            </div>
          </section>

          {/* Visual divider */}
          <div className="tu-takeawayDivider" />

          {/* Professional standards explanation */}
          <section className="tu-takeawaySection">
            <h3 className="tu-takeawayH3 tu-takeawayH3--main">
                Why Auditor Independence Matters
            </h3>

            <p className="tu-takeawayP">
              Auditor independence is at the core of our profession and is a principle embedded in both the American Institute of Certified Public Accountants (AICPA)Code of Professional Conduct and the Generally Accepted Government Auditing Standards (GAGAS), also known as the Yellow Book, issued by the U.S. Government Accountability Office (GAO). These standards make clear that independence is not optional—it is an ethical and professional imperative.
            </p>

            <p className="tu-takeawayP">
              Independence matters for several reasons:
            </p>

            {/* List of independence reasons */}
            <div className="tu-takeawayReasons tu-takeawayReasons--full">
              {reasons.map((r) => (
                <article key={r.number} className="tu-takeawayReason">
                  <div className="tu-takeawayReasonTop">
                    <span className="tu-takeawayReasonNum">{r.number}</span>
                    <strong className="tu-takeawayReasonTitle">
                      {r.title}
                    </strong>
                  </div>

                  <p className="tu-takeawayReasonText">{r.body}</p>
                </article>
              ))}
            </div>
          </section>

          {/* Visual divider */}
          <div className="tu-takeawayDivider" />

          {/* Shared responsibility section */}
          <section className="tu-takeawaySection tu-takeawaySection--panel">
            <h3 className="tu-takeawayH3">
              Shared Responsibility for Independence
            </h3>

            <p className="tu-takeawayP">
              While independence is a professional obligation of the auditor, maintaining it is a shared responsibility. The entity being audited, its governing board and/or its own internal auditors all play roles in protecting and ensuring independence throughout the auditing process. Independence is fundamental to auditor credibility and cannot be compromised by convenience or pressure.
            </p>
          </section>

          {/* Visual divider */}
          <div className="tu-takeawayDivider" />

          {/* Independence in practice section */}
          <section className="tu-takeawaySection tu-takeawaySection--panel">
            <h3 className="tu-takeawayH3">Independence in Practice</h3>

            <p className="tu-takeawayP">
              In our Office, independence allows us to conduct proactive reviews without waiting for complaints. These reviews often identify risks and compliance issues early, enabling corrective actions before they escalate into larger problems. Our reports provide constructive recommendations, not merely findings, to strengthen internal controls and promote effective governance.
            </p>

            <p className="tu-takeawayP">
              When performing audits, we adhere strictly to independence requirements, including assessing personal, external, and organizational impairments. Our role is not to align with any political agenda or administrative preference, but to deliver objective, fact and evidence-based findings that serve our client, the people of Utah.
            </p>
          </section>

          {/* Visual divider */}
          <div className="tu-takeawayDivider" />

          {/* Conclusion section */}
          <section className="tu-takeawaySection tu-takeawaySection--panel">
            <h3 className="tu-takeawayH3">Conclusion</h3>

            <p className="tu-takeawayP">
              For CPAs, independence is more than a compliance box—it is at the core of the profession. In public service auditing, it is indispensable for accountability, transparency, and trust. As the Utah State Auditor, I am committed to upholding independence in both action and appearance, ensuring that our work enhances the reliability of financial reporting, continues to protect taxpayers, strengthens and builds public confidence, and promotes integrity and transparency in government.
            </p>

            <p className="tu-takeawayP tu-mb0">
              Independence is not just a professional obligation; it is key to the public’s trust. By guarding it carefully, we uphold the credibility of our work and fulfill our ultimate responsibility, protecting the public we serve.
            </p>
          </section>

        </div>
      </div>
    </section>
  );
};

export default TaxpayersTakeaway;